import React from 'react'
import {
  useResponsiveDropdown,
  useBreakpoint,
  SFSymbolLabel,
} from '@sketch/components'

import {
  CanvasSection,
  CanvasLinks,
  useCanvasHost,
  useCanvasSiblingSections,
} from '../../../../canvas/host'
import { useCanvasScope } from '../../../../canvas/scope'
import {
  Trigger,
  CurrentSection,
  Chevrons,
  Container,
  StyledDropdownNavItem,
} from './SectionSelector.styles'

const DROPDOWN_STYLES = {
  minWidth: 160,
  overflowY: 'auto',
  maxHeight: '80vh',
} as const

interface SectionListProps {
  /** Absent only before the host resolves, when the trigger is disabled. */
  links: CanvasLinks | undefined
  pageUUID: string
  currentSectionUUID: string
  sections: CanvasSection[]
}

function SectionList({
  links,
  pageUUID,
  currentSectionUUID,
  sections,
}: SectionListProps) {
  if (!links) {
    return null
  }

  return (
    <>
      {sections.map(section => {
        const pathname = links.section(pageUUID, section.uuid)

        if (!pathname) {
          return null
        }

        return (
          <StyledDropdownNavItem
            key={section.uuid}
            data-testid="section-selector-item"
            to={pathname}
            isActive={match =>
              Boolean(match) || section.uuid === currentSectionUUID
            }
            exact
          >
            <SFSymbolLabel text={section.name ?? ''} />
          </StyledDropdownNavItem>
        )
      })}
    </>
  )
}

/**
 * Switches between the focused Section's siblings from the canvas panel.
 *
 * Reads the page and the focused Section from the canvas scope, and both the
 * sibling list and the links from the host - so the same component serves a
 * Share and a Preview, which previously had a copy each.
 */
export function SectionSelector() {
  const host = useCanvasHost()
  const scope = useCanvasScope()

  const pageUUID = scope?.pageUUID ?? ''
  const currentSectionUUID =
    scope?.focus.kind === 'section' ? scope.focus.uuid : ''

  const sections = useCanvasSiblingSections(pageUUID, currentSectionUUID)
  const currentSection = sections.find(
    section => section.uuid === currentSectionUUID
  )
  const isMobile = !useBreakpoint('sm')

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: SectionList,
    dropdownProps: {
      links: host?.links,
      pageUUID,
      currentSectionUUID,
      sections,
    },
    usePortal: true,
    placement: isMobile ? 'top-start' : 'bottom-start',
    dropdownStyle: DROPDOWN_STYLES,
    offset: [-8, 13], // 13 = 5 (usual offset) + 8 (canvas panel bottom padding)
  })

  const isDisabled = !host || sections.length <= 1

  return (
    <Container>
      <Trigger
        {...buttonProps}
        aria-label="Canvas sections list"
        data-testid="sections-selector-trigger"
        disabled={isDisabled}
      >
        <CurrentSection>
          {currentSection ? (
            <SFSymbolLabel text={currentSection.name ?? ''} />
          ) : (
            'Select section...'
          )}
        </CurrentSection>
        {!isDisabled && <Chevrons />}
      </Trigger>
      {!isDisabled && content}
    </Container>
  )
}
